import { useEffect, useMemo, useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Helmet } from "react-helmet-async"
import { ArrowRight, BookOpen, Users, Shield, GraduationCap, UserCircle, UsersRound } from "lucide-react"
import { loadPublicAuthors } from "../store/slices/publicSlice" 

const ROLE_GROUP_KEYS = [
  { key: 'all', label: 'Tous', icon: Users },
  { key: 'administrationModeration', label: 'Administration et modération', icon: Shield },
  { key: 'professors', label: 'Professeurs', icon: GraduationCap },
  { key: 'spaceStudents', label: 'Espace étudiant', icon: UserCircle },
  { key: 'students', label: 'Étudiants', icon: UsersRound },
]

const roleLabels = { professor: 'Professeur', student: 'Étudiant', 'space-student': 'Espace Étudiant', admin: 'Administrateur', moderator: 'Modérateur' }

function AuthorCard({ a }) {
  return (
    <Link
      to={`/auteur/${a._id}`}
      className="group bg-card rounded-xl border border-border p-6 hover:shadow-lg hover:border-primary/30 transition-all flex flex-col items-center text-center"
    >
      {a.profilePicture ? (
        <img src={a.profilePicture} alt={a.fullname} className="w-20 h-20 rounded-full object-cover mb-4 border-2 border-border group-hover:border-primary/30 transition-colors" />
      ) : (
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-2xl font-bold mb-4">
          {a.fullname.charAt(0)}
        </div>
      )}
      <h2 className="text-lg font-bold text-foreground mb-1 group-hover:text-primary transition-colors">{a.fullname}</h2>
      {(a.role || a.department) && (
        <p className="text-xs text-muted-foreground mb-2">
          {[roleLabels[a.role] || a.role, a.department].filter(Boolean).join(' · ')}
        </p>
      )}
      <p className="text-sm text-primary font-semibold flex items-center gap-1">
        <BookOpen className="w-4 h-4" />
        {a.postCount ?? 0} article{a.postCount !== 1 ? 's' : ''} publié{a.postCount !== 1 ? 's' : ''}
      </p>
      <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
        Voir le profil
        <ArrowRight className="w-4 h-4" />
      </span>
    </Link>
  )
}

export default function AuthorList() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { authorsByRole, loading } = useSelector((s) => s.public)
  const [activeGroup, setActiveGroup] = useState('all')

  useEffect(() => {
    dispatch(loadPublicAuthors())
  }, [dispatch])

  const authorsByGroup = useMemo(() => {
    const adm = authorsByRole?.administrationModeration ?? []
    const prof = authorsByRole?.professors ?? []
    const space = authorsByRole?.spaceStudents ?? []
    const stud = authorsByRole?.students ?? []
    return {
      all: [...adm, ...prof, ...space, ...stud],
      administrationModeration: adm,
      professors: prof,
      spaceStudents: space,
      students: stud,
    }
  }, [authorsByRole])

  const currentAuthors = authorsByGroup[activeGroup] ?? []
  const hasAny = authorsByGroup.all.length > 0

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Tous les auteurs - Université de Ngozi</title>
        <meta name="description" content="Découvrez les auteurs et contributeurs des actualités de l'Université de Ngozi." />
      </Helmet>

      <section className="relative py-16 bg-gradient-to-br from-primary/5 via-background to-accent/5 overflow-hidden">
        <div className="absolute top-10 right-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 relative">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
            Retour
          </button>
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-10 h-10 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Tous les auteurs</h1>
          </div>
          <p className="text-muted-foreground mb-6">Retrouvez les contributeurs des actualités et articles de l'Université de Ngozi.</p>

          {hasAny && (
            <div className="flex flex-wrap gap-2">
              {ROLE_GROUP_KEYS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveGroup(key)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${activeGroup === key ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-secondary text-foreground border border-border hover:border-primary/30 hover:bg-primary/5'}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-48 bg-secondary rounded-xl animate-pulse" />
              ))}
            </div>
          ) : activeGroup === 'all' && hasAny ? (
            <>
              {(authorsByRole?.administrationModeration?.length > 0) && (
                <div className="mb-10">
                  <h2 className="flex items-center gap-2 text-lg font-bold text-foreground mb-4">
                    <Shield className="w-5 h-5 text-primary" />
                    Administration et modération
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {authorsByRole.administrationModeration.map((a) => (
                      <AuthorCard key={a._id} a={a} />
                    ))}
                  </div>
                </div>
              )}
              {(authorsByRole?.professors?.length > 0) && (
                <div className="mb-10">
                  <h2 className="flex items-center gap-2 text-lg font-bold text-foreground mb-4">
                    <GraduationCap className="w-5 h-5 text-primary" />
                    Professeurs
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {authorsByRole.professors.map((a) => (
                      <AuthorCard key={a._id} a={a} />
                    ))}
                  </div>
                </div>
              )}
              {(authorsByRole?.spaceStudents?.length > 0) && (
                <div className="mb-10">
                  <h2 className="flex items-center gap-2 text-lg font-bold text-foreground mb-4">
                    <UserCircle className="w-5 h-5 text-primary" />
                    Espace étudiant
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {authorsByRole.spaceStudents.map((a) => (
                      <AuthorCard key={a._id} a={a} />
                    ))}
                  </div>
                </div>
              )}
              {(authorsByRole?.students?.length > 0) && (
                <div className="mb-10">
                  <h2 className="flex items-center gap-2 text-lg font-bold text-foreground mb-4">
                    <UsersRound className="w-5 h-5 text-primary" />
                    Étudiants
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {authorsByRole.students.map((a) => (
                      <AuthorCard key={a._id} a={a} />
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : !hasAny ? (
            <div className="text-center py-16 bg-card rounded-xl border border-border">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun auteur pour le moment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentAuthors.map((a) => (
                <AuthorCard key={a._id} a={a} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
